import { BarScore, RadialScore } from "@/components/ScoreMeter";
import ProvenanceTag from "@/components/ProvenanceTag";
import { CheckCircle2, AlertTriangle, Wrench, Target, DollarSign, FlaskConical, Users, CalendarDays } from "lucide-react";

function Block({ icon: Icon, title, children, tone = "cyan" }) {
  const c = { cyan: "text-cyan-400", green: "text-emerald-400", amber: "text-amber-400" }[tone];
  return (
    <div className="glass rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3"><Icon className={`w-4 h-4 ${c}`} /><h4 className="font-semibold text-slate-100">{title}</h4></div>
      {children}
    </div>
  );
}
function List({ items, tone = "cyan" }) {
  const c = { cyan: "text-cyan-500", green: "text-emerald-500", amber: "text-amber-500" }[tone];
  return <ul className="space-y-1.5">{(items || []).map((it, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className={`${c} mt-1`}>▸</span>{it}</li>)}</ul>;
}

export default function ValidationReport({ report }) {
  if (!report) return null;
  const s = report.scores || {};
  return (
    <div className="space-y-6 fade-up" data-testid="validation-report">
      <div className="glass rounded-2xl p-6 glow-cyan flex flex-col sm:flex-row items-center gap-6">
        <RadialScore value={report.overall || 0} size={120} stroke={10} label="Overall" testId="validation-overall" />
        <div className="flex-1 text-center sm:text-left">
          <ProvenanceTag type="ai" />
          <h3 className="font-display text-2xl font-bold text-slate-50 mt-2">{report.verdict}</h3>
        </div>
      </div>

      <div className="glass rounded-xl p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <BarScore label="Opportunity" value={s.opportunity} />
        <BarScore label="Demand" value={s.demand} />
        <BarScore label="Competition" value={s.competition} invert />
        <BarScore label="Monetization" value={s.monetization} />
        <BarScore label="Build Complexity" value={s.build_complexity} invert />
        <BarScore label="Customer Pain" value={s.customer_pain} />
        <BarScore label="Differentiation" value={s.differentiation} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Block icon={CheckCircle2} title="What looks promising" tone="green"><List items={report.promising} tone="green" /></Block>
        <Block icon={AlertTriangle} title="What concerns us" tone="amber"><List items={report.concerns} tone="amber" /></Block>
        <Block icon={Wrench} title="What would make this stronger"><List items={report.make_stronger} /></Block>
        <Block icon={FlaskConical} title="Validation experiments"><List items={report.validation_experiments} /></Block>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Block icon={Target} title="Target first"><p className="text-sm text-slate-300">{report.target_first}</p></Block>
        <Block icon={Wrench} title="Recommended MVP"><p className="text-sm text-slate-300">{report.recommended_mvp}</p></Block>
        <Block icon={DollarSign} title="Recommended pricing"><p className="text-sm text-slate-300">{report.recommended_pricing}</p></Block>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Block icon={Users} title="First 10 customers"><p className="text-sm text-slate-300">{report.first_ten_customers}</p></Block>
        <Block icon={CalendarDays} title="90-day launch plan"><List items={report.ninety_day_plan} /></Block>
      </div>
    </div>
  );
}
